import { MetricDelta } from './types'
import { localeForCurrency } from '../../utils/currencyLocale'

interface DeltaIndicatorProps {
  delta: MetricDelta
  inverse?: boolean  // If true, "down" is good (e.g., PPM, NCRs)
  format?: 'number' | 'currency' | 'percentage'
  currency?: string
}

/**
 * DeltaIndicator Component
 *
 * Displays month-over-month change for KPI metrics with visual indicators
 *
 * Features:
 * - Trend arrows (↑↓→)
 * - Color coding based on improvement/decline
 * - Percentage change display
 * - Supports inverse metrics (lower is better)
 *
 * Phase 5: Month-over-month delta indicators
 */
export function DeltaIndicator({ delta, inverse = false, format = 'number', currency = 'GBP' }: DeltaIndicatorProps) {
  if (delta && (delta.change === null || delta.previous === null || delta.percentage_change === null)) {
    return (
      <div className="text-xs text-muted-foreground mt-1">
        {/* CR-8867: say which month has nothing to compare */}
        No comparison — {delta.current === null ? 'this' : 'last'} month N/A
      </div>
    )
  }
  // CR-8867: a rate measured at 0 last month (CR-7714) is a real baseline, but
  // a percentage change from zero is undefined — show the change itself.
  const fromZero = delta?.previous === 0 && (delta.change ?? 0) !== 0
  if (!delta || (delta.percentage_change === 0 && !fromZero)) {
    return (
      <div className="text-xs text-muted-foreground mt-1">
        → No change vs last month
      </div>
    )
  }

  // Determine if the change is good or bad
  const isImproving = inverse
    ? delta.trend === 'down'  // For PPM, NCRs: down is good
    : delta.trend === 'up'     // For OTIF, OTD: up is good
  // CR-8867: a 'stable' move (under 10%) is neither good nor bad news.
  const isNeutral = delta.trend !== 'up' && delta.trend !== 'down'

  // Color classes
  const colorClass = isNeutral
    ? 'text-muted-foreground'
    : isImproving
      ? 'text-green-600 dark:text-green-400'
      : 'text-red-600 dark:text-red-400'

  // Arrow icon
  const arrow = delta.trend === 'up' ? '↑' : delta.trend === 'down' ? '↓' : '→'

  // Format the change value
  const formatChange = (value: number) => {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat(localeForCurrency(currency), {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Math.abs(value))
      case 'percentage':
        return `${Math.abs(value).toFixed(1)}%`
      default:
        return Math.abs(value).toLocaleString()
    }
  }

  return (
    <div className={`text-xs font-medium mt-1 ${colorClass}`}>
      {fromZero
        ? `${arrow} ${formatChange(delta.change ?? 0)} vs last month`
        : `${arrow} ${Math.abs(delta.percentage_change ?? 0).toFixed(1)}% (${formatChange(delta.change ?? 0)}) vs last month`}
    </div>
  )
}
